import React, { useContext, useEffect, useRef, useState } from "react";

import { useNavigate, Link } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff } from "lucide-react";
// import Input from "@material-ui/core/Input";
import { Input } from "../ui/input";
import { Button } from "../ui/button";

import { loginWithEmail, logOut, updateUserSession } from "../../services/firebase";
import AuthContext from "../../hooks/authContext";
import validator from "validator";
import { findClientsByEmail, saveLogins, syncClientContactLogin } from "../../services/apiservices/client";
import { DOMAIN_TYPE, DOMAIN_TYPES } from "../../services/config";
import { CMS_EVENTS, trackEvent } from "../../services/apiservices/analytics";
import { getDeviceInfo, markFreshAuthFlow, resolveServerSessionId } from "../../utils/sessionDeviceInfo";
import { clearSessionEnteredViaForceLogin, setSessionEnteredViaForceLogin } from "../../utils/sessionFlags";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alertDialog";
// import { Button } from "../";
// import { Input } from "@/components/ui/input";
// import { useToast } from "@/hooks/use-toast";
// import { useAuth } from "@/contexts/AuthContext";

const LOGIN_SESSION_RESET_KEYS = [
  "sessionId",
  "sessionSyncAt",
  "authFlowAt",
  "pcIdleExpiry",
  "pcAbsoluteExpiry",
];

const LoginForm = (props) => {
  const authInputClassName =
    "pl-40 bg-white text-[#0f172a] border-gray-300 placeholder:text-gray-500 focus-visible:ring-[#2980ba]";
  const navigate = useNavigate();
  // const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  // const { login } = useAuth();
  const auth = useContext(AuthContext);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showForceLoginDialog, setShowForceLoginDialog] = useState(false);
  const [pendingLoginData, setPendingLoginData] = useState(null);
  const [existingDevice, setExistingDevice] = useState(null);
  const [state, setState] = useState({
    formState: { username: "", password: "" },
    loading: false,
    sRedirect: "",
    remember: "",
  });
  /** Clears button loading when App auth finishes after auth.login() */
  const awaitingAuthCompleteRef = useRef(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name !== "") {
      setState({ ...state, formState: { ...state.formState, [name]: value } });
    }
  };
  const setInitState = () => {
    setState({
      ...state,
      remember: localStorage.getItem("remember") === "true",
      sRedirect: "",
      formState: {
        ...state.formState,
        username: localStorage.getItem("username") || "",
        // password: localStorage.password,
      },
    });
  };
  useEffect(() => {
    if (localStorage.getItem("remember") === "true" && (localStorage.getItem("username") || "") !== "") {
      setInitState();
    }
  }, []);
  const redirecifloggedin = () => {
    navigate("/dashboard");
  };
  useEffect(() => {
    if (auth.status === true) {
      redirecifloggedin();
    }
  }, [auth.status]);

  useEffect(() => {
    if (!awaitingAuthCompleteRef.current) return;
    // Clear button as soon as session is recognized — don’t wait for background isLoading
    if (auth.status === true) {
      awaitingAuthCompleteRef.current = false;
      setState((prev) => ({ ...prev, loading: false }));
      return;
    }
    if (!auth.isLoading && auth.status !== true) {
      awaitingAuthCompleteRef.current = false;
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, [auth.status, auth.isLoading]);

  const onChangeCheckbox = (event) => {
    setState({ ...state, remember: event.target.checked });
  };

  const isRoleAllowedForDomain = (userRoles = []) => {
    const isClient = userRoles.includes("Client");
    const isSuperUser = userRoles.includes("superUser");

    switch (DOMAIN_TYPE) {
      case DOMAIN_TYPES.SUPERUSER_ONLY:
        return isSuperUser;

      case DOMAIN_TYPES.CLIENT_ONLY:
        return isClient;

      case DOMAIN_TYPES.ALL:
      default:
        return true;
    }
  };

  const normalizeRoles = (roleValue) => {
    if (Array.isArray(roleValue)) return roleValue.filter(Boolean);
    return roleValue ? [roleValue] : [];
  };

  const resetLocalLoginSessionState = () => {
    LOGIN_SESSION_RESET_KEYS.forEach((key) => localStorage.removeItem(key));
  };

  const handleForceLogin = async () => {
    if (!pendingLoginData) return;

    try {
      setState((prev) => ({ ...prev, loading: true }));
      resetLocalLoginSessionState();

      const deviceInfo =
        pendingLoginData.__deviceInfoForForceLogin || (await getDeviceInfo());

      const res = await updateUserSession(pendingLoginData.userId, deviceInfo, true);

      if (!res.status) {
        throw new Error("Session update failed");
      }

      const sessionId = resolveServerSessionId(res);
      markFreshAuthFlow(sessionId);
      setSessionEnteredViaForceLogin();

      awaitingAuthCompleteRef.current = true;
      auth.login(pendingLoginData.token, {
        preloadedUserSnapshot: pendingLoginData.userData
          ? { userData: pendingLoginData.userData, token: pendingLoginData.token }
          : undefined,
      });

      const pendingRoles = normalizeRoles(pendingLoginData.role);
      if (pendingRoles.includes("superUser")) {
        navigate("/superUserDashboard");
      } else {
        navigate("/dashboard");
      }

      postLoginProcess(pendingLoginData).catch(() => {});
    } catch (error) {
      setErrorMessage("Failed to establish new session.");
      setState((prev) => ({ ...prev, loading: false }));
    } finally {
      setShowForceLoginDialog(false);
      setPendingLoginData(null);
    }
  };

  const handleCancelLogin = async () => {
    setShowForceLoginDialog(false);
    setPendingLoginData(null);
    localStorage.removeItem("forceLoginPending");

    try {
      await logOut();
    } catch (error) { }
  };

  const postLoginProcess = async (loginRes) => {
    saveLogins(loginRes.userId);
    syncClientContactLogin(loginRes.userId, loginRes.user?.email);

    const data = await findClientsByEmail(loginRes?.user?.email);

    if (data?.clients?.length > 0 && data?.companies?.length > 0) {
      localStorage.setItem("companies", JSON.stringify(data.companies));
      localStorage.setItem("clients", JSON.stringify(data.clients));
      localStorage.setItem("companyId", data.clients[0]?.companyId);
      localStorage.setItem("clientId", data.clients[0]?.id);
    }

    trackEvent(CMS_EVENTS.LOGIN_SUCCESS, {
      user_id: loginRes?.userId,
    });
  };

  const userlogin = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");
    localStorage.removeItem("forceLoginPending");
    resetLocalLoginSessionState();

    const { remember } = state;
    let { username, password } = state.formState;
    username = username?.trim();
    password = password?.trim();

    if (!username || !password) {
      setErrorMessage("Email and Password are required");
      return;
    }

    if (remember) {
      localStorage.setItem("username", username);
      localStorage.setItem("remember", "true");
    } else {
      localStorage.removeItem("username");
      localStorage.removeItem("remember");
    }

    if (!validator.isEmail(username)) {
      setErrorMessage("Invalid email address");
      return;
    }

    let deviceInfoPromise;
    try {
      setState((prev) => ({ ...prev, loading: true }));

      // Device fingerprint does not depend on credentials — overlap with sign-in to cut serial wait.
      deviceInfoPromise = getDeviceInfo();
      const loginRes = await loginWithEmail(username, password);

      trackEvent(CMS_EVENTS.USER_LOGIN, {
        user_id: auth?.userProfile?.id || loginRes?.userId,
      });

      if (!loginRes?.token) {
        await deviceInfoPromise.catch(() => null);
        setErrorMessage("Email or password does not match");

        trackEvent(CMS_EVENTS.LOGIN_FAILED, {
          user_id: auth?.userProfile?.id,
        });
        setState((prev) => ({ ...prev, loading: false }));
        return;
      }

      const role = loginRes?.role;
      const userRoles = normalizeRoles(role);

      if (!isRoleAllowedForDomain(userRoles)) {
        await deviceInfoPromise.catch(() => null);
        setErrorMessage("You are not allowed to login from this domain");
        setState((prev) => ({ ...prev, loading: false }));
        return;
      }

      const deviceInfo = await deviceInfoPromise;
      const sessionRes = await updateUserSession(loginRes.userId, deviceInfo);

      if (!sessionRes.status && sessionRes.existingSession) {
        // Prevent App.js from automatically logging in while dialog is open
        localStorage.setItem("forceLoginPending", "true");

        setExistingDevice(sessionRes.existingSession);
        setPendingLoginData({
          ...loginRes,
          __deviceInfoForForceLogin: deviceInfo,
        });
        setShowForceLoginDialog(true);

        // Do NOT call logOut() here. Prematurely logging out kills the Firebase session 
        // we just created, which prevents the subsequent Force Login from succeeding.
        // We only logout if the user explicitly cancels.
        setState((prev) => ({ ...prev, loading: false }));
        return;
      }

      if (!sessionRes?.status) {
        setErrorMessage("Unable to establish session. Please try again.");
        setState((prev) => ({ ...prev, loading: false }));
        try {
          await logOut();
        } catch (_e) {}
        return;
      }

      const sessionId = resolveServerSessionId(sessionRes);
      markFreshAuthFlow(sessionId);
      clearSessionEnteredViaForceLogin();
      awaitingAuthCompleteRef.current = true;
      auth.login(loginRes.token, {
        preloadedUserSnapshot: {
          userData: loginRes.userData,
          token: loginRes.token,
        },
      });
      postLoginProcess(loginRes).catch(() => {});
    } catch (error) {
      await deviceInfoPromise?.catch(() => null);
      setErrorMessage("Login failed. Please try again.");
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  const { formState, loading, remember, sRedirect } = state;
  const { classes } = props;

  // Simplified login - no validation or authentication required
  const handleLogin = () => {
    // // Just log in without checking credentials
    // login("demo@proteams.io", "demo-password");

    // toast({
    //   title: "Welcome back!",
    //   description: "You've successfully logged in.",
    // });
    navigate("/");
  };

  return (
    <form onSubmit={userlogin} className="space-y-5 sm:space-y-6 w-full px-4 sm:px-0">
      {/* Email field - no validation */}
      <div className="space-y-2">
        <label htmlFor="email" className="block text-sm font-medium">
          Email Address
        </label>
        <div className="relative">
          <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            fullWidth={true}
            disableUnderline={true}
            onChange={handleInputChange}
            value={formState.username}
            autoComplete="email"
            className={authInputClassName}
            name="username"
            id="username"
            placeholder="Email"
          />
        </div>
      </div>

      {/* Password field - no validation */}
      <div className="space-y-2">
        <div className="flex justify-between items-baseline">
          <label htmlFor="password" className="block text-sm font-medium">
            Password
          </label>
          <Link
            to="/forgotpassword"
            className="text-sm text-green-600 hover:text-green-800"
          >
            Forgot password?
          </Link>
        </div>
        <div className="relative">
          <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            fullWidth={true}
            disableUnderline={true}
            onChange={handleInputChange}
            value={formState.password}
            autoComplete="current-password"
            className={authInputClassName}
            name="password"
            id="password"
            type={showPassword ? "text" : "password"}
            placeholder="Password"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>

      {/* Login button - no form validation */}

      <Button
        type="submit"
        className="w-full"
        size="lg"
        disabled={loading ? true : false}
      >
        {loading ? "Loading..." : "LOG IN"}
      </Button>
      {errorMessage !== "" && (
        <div className="text-center text-red-600 text-sm font-medium mt-4">{errorMessage}</div>
      )}

      <div className="text-center text-sm text-gray-500">
        Don't have an account?{" "}
        <Link
          to="/register"
          className="text-green-600 hover:text-green-800 font-medium"
        >
          Start your free trial
        </Link>
      </div>

      <AlertDialog open={showForceLoginDialog} onOpenChange={setShowForceLoginDialog}>
        <AlertDialogContent className="max-w-md">

          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-semibold">
              Account Already Logged In
            </AlertDialogTitle>

            <AlertDialogDescription className="space-y-3">

              <p>
                Your account is currently active on another device.
              </p>

              {existingDevice && (
                <div className="rounded-lg border bg-gray-50 p-16 text-sm space-y-1">

                  <div className="font-medium mb-4">
                    Active Device
                  </div>

                  <div className="flex items-center gap-2 font-medium">
                    {existingDevice.deviceType === "mobile" ? "📱" : "🖥"}
                    <span>{existingDevice.os}</span>
                  </div>

                  <div className="text-gray-600">
                    Browser: {existingDevice.browser}
                  </div>

                  {existingDevice.location && (
                    <div className="text-gray-500">
                      {existingDevice.location}
                    </div>
                  )}

                </div>
              )}

              <p className="text-xs text-gray-500">
                If you continue, the other device will be logged out.
              </p>

            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelLogin} disabled={loading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleForceLogin}
              className="bg-red-600 hover:bg-red-700"
              disabled={loading}
            >
              {loading ? "Establishing Session..." : "Force Login"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </form>
  );
};

export default LoginForm;
